import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AgGrid from '../../common/AgGrid';
import ContactModal from '../../common/AddContactModal';
import SuccessMessage from '../../common/SuccessMessage';
import { AisScreenLoader } from '../../../utils/AisScreenLoader';
import Icon from '../../common/Icon';
import {
  GetClientPortal,
  GetParticularClientList,
  GetClientContact,
  GetClientContactDetailsById,
  MarkFavorite,
  UploadClientProfilePicture,
  GetClientProfilePicture,
  DeleteClientProfile,

} from '../../../services/accountService';
import Tallies from '../Tallies';
import Jobs from '../Jobs';
import Summary from './Tabs/Summary';
import Matrics from './Tabs/Metrics';
import { canViewField, isListAgent, isRestrictedUser } from '../../../utils/PermissionUtils';
// ✅ NEW: RBAC field-level permission helpers

const ClientDashboardMain = () => {
  const location = useLocation();

  const navigate = useNavigate();

  // ========== RBAC: FIELD-LEVEL PERMISSION FLAGS ==========
  // Computed once per render from localStorage 'userPermissions'.
  // Default is TRUE when no rule exists (internal users have no restriction rows).
  // ========== RBAC: FIELD-LEVEL PERMISSION FLAGS ==========
  // ✅ RBAC: Consultant (and Client, via the same restricted-user check) must not
  //    see Accounting Details or Contract Information on the Summary tab —
  //    forced off regardless of what canViewField() returns for these fields.
  const isConsultantOrClient = isRestrictedUser();
  const fieldPerms = {
    creativeFee: canViewField('creativefee') && !isConsultantOrClient,
    designFee: canViewField('designfee') && !isConsultantOrClient,
    contractDate: canViewField('contractdate') && !isConsultantOrClient,
    sageClientCode: canViewField('sageclientcode') && !isConsultantOrClient,
    salesCommission: canViewField('salescommission') && !isConsultantOrClient,
    accountingType: canViewField('accountingtype') && !isConsultantOrClient,
    notes: canViewField('notes'),
    postalInformation: canViewField('postalinformation'),
    internalRolesPanel: canViewField('internalrolespanel'),
    averageGift: canViewField('averagegift'),
    totalDonors: canViewField('totaldonors'),
    totalIncome: canViewField('totalincome'),
  };
  // ✅ RBAC: Client, Consultant, and List Agent users cannot edit/upload/remove
  //    the client profile picture — view-only.
  const canEditProfilePicture = !isListAgent() && !isRestrictedUser();

  // Metrics tab renders grossCost / netRevenue / ROI which are internal-only.
  // 'cost' on Jobs → JobList is the proxy rule for internal financial metrics.
  const canViewMetricsTab =
    canViewField('cost') && canViewField('net') && canViewField('roi');

  // State variables
  const [activeTab, setActiveTab] = useState('summary');
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedContact, setSelectedContact] = useState(null);
  const [contacts, setContacts] = useState([]);

  const [loadingProfileImage, setLoadingProfileImage] = useState(false);
  const [deletingImage, setDeletingImage] = useState(false);

  // Loading state for contact details API
  const [loadingContactDetails, setLoadingContactDetails] = useState(false);

  // Toast notification state - ONLY for favorite/unfavorite
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  // ✅ NEW: Toast/modal message state - for Add/Edit Contact success & warning
  // Uses the shared SuccessMessage component. 'success' -> green success toaster,
  // 'error' -> amber warning toaster.
  const [contactMessage, setContactMessage] = useState({
    show: false,
    message: '',
    type: 'success', // 'success' | 'error'
  });

  const closeContactMessage = () =>
    setContactMessage({ show: false, message: '', type: 'success' });

  // API State variables
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  const [errorContacts, setErrorContacts] = useState(null);
  const [updatingFavorite, setUpdatingFavorite] = useState(false);
  const dropdownRef = useRef(null);
  const itemRefs = useRef({});
  const searchBufferRef = useRef('');
  const searchTimeoutRef = useRef(null);
  const [highlightedId, setHighlightedId] = useState(null);

  const [profileImage, setProfileImage] = useState(null);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const fileInputRef = useRef();

  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  // ✅ RBAC: if user landed on a tab they can't view (e.g. via stale state), bounce to summary
  useEffect(() => {
    if (activeTab === 'metrics' && !canViewMetricsTab) {
      setActiveTab('summary');
    }
  }, [activeTab, canViewMetricsTab]);

  const getWhatsAppAvatarDataUri = () => {
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200">
        <circle cx="100" cy="100" r="100" fill="#DFE5E7"/>
        <circle cx="100" cy="85" r="32" fill="#FFFFFF"/>
        <path d="M40,180 C40,140 60,125 100,125 C140,125 160,140 160,180 Z" fill="#FFFFFF"/>
      </svg>`;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  };

  const avatarImage = getWhatsAppAvatarDataUri();

  const imageToShow = profileImage || avatarImage;

  const hasRealProfileImage = Boolean(profileImage);

  const getProfileImageKey = (clientId) => `client-profile-image-${clientId}`;

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ["image/jpeg", "image/png"];
    const allowedExtensions = [".jpg", ".jpeg", ".png"];

    const fileExtension = file.name
      .toLowerCase()
      .substring(file.name.lastIndexOf("."));

    // ✅ Validate file type (MIME + extension)
    if (!allowedTypes.includes(file.type) || !allowedExtensions.includes(fileExtension)) {
      showNotification("Invalid file format. Only JPG and PNG allowed.", "error");
      e.target.value = null;
      return;
    }

    // ✅ Validate file size
    if (file.size > maxSize) {
      showNotification("File size should not exceed 5MB.", "error");
      e.target.value = null;
      return;
    }

    // ✅ If valid → proceed
    setSelectedImageFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setProfileImage(reader.result);
    };
    reader.readAsDataURL(file);
  };


  // Focus the dropdown when it opens so it can receive key events
  useEffect(() => {
    if (isDropdownOpen && dropdownRef.current) {
      dropdownRef.current.focus();
      setHighlightedId(selectedClient?.id ?? null);
    } else {
      setHighlightedId(null);
      searchBufferRef.current = '';
    }
  }, [isDropdownOpen, selectedClient]);

  // Scroll the highlighted item into view
  useEffect(() => {
    if (highlightedId && itemRefs.current[highlightedId]) {
      itemRefs.current[highlightedId].scrollIntoView({
        block: 'nearest',
      });
    }
  }, [highlightedId]);

  const handleKeyDown = (e) => {
    if (!isDropdownOpen) return;

    // Arrow keys for up/down navigation
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const currentIndex = clients.findIndex((c) => c.id === highlightedId);
      let nextIndex;
      if (e.key === 'ArrowDown') {
        nextIndex = currentIndex < clients.length - 1 ? currentIndex + 1 : 0;
      } else {
        nextIndex = currentIndex > 0 ? currentIndex - 1 : clients.length - 1;
      }
      setHighlightedId(clients[nextIndex].id);
      return;
    }

    // Enter to select
    if (e.key === 'Enter' && highlightedId) {
      e.preventDefault();
      const client = clients.find((c) => c.id === highlightedId);
      if (client) handleClientSelect(client);
      return;
    }

    // Escape to close
    if (e.key === 'Escape') {
      e.preventDefault();
      setIsDropdownOpen(false);
      return;
    }

    // Letter/number keys: jump to matching item (type-ahead)
    if (e.key.length === 1 && /\S/.test(e.key)) {
      e.preventDefault();

      // Build up a search buffer so typing "Sm" finds "Smith" not "S..." then "M..."
      searchBufferRef.current += e.key.toLowerCase();

      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => {
        searchBufferRef.current = '';
      }, 500);

      const buffer = searchBufferRef.current;
      const match = clients.find((c) =>
        c.name.toLowerCase().startsWith(buffer)
      );

      if (match) setHighlightedId(match.id);
    }
  };


  // Get initial clientId from navigation state (when coming from Dashboard)
  const initialClientId = location.state?.clientId;

  // Favorite info from navigation state
  const initialFavoriteInfo = location.state?.favoriteInfo;
  const allFavoritesFromState = location.state?.allFavorites || [];
  const favoritesStateFromProps = location.state?.favoritesState || {};

  // Show notification function - ONLY for favorite/unfavorite
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  // Notification Component - ONLY for favorite/unfavorite
  const Notification = () => {
    if (!notification.show) return null;

    const backgroundColor = notification.type === 'error' ? '#F44336' : '#4CAF50';

    return (
      <div
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '12px 20px',
          borderRadius: '4px',
          color: 'white',
          fontWeight: '500',
          zIndex: '1000',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
          backgroundColor,
          fontSize: '14px',
          animation: 'slideIn 0.3s ease-out'
        }}
      >
        {notification.message}
      </div>
    );
  };

  // Add CSS animation for notification
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Form data state
  const [formData, setFormData] = useState({
    uspsAuthCode: '',
    cagingPOBox: '',
    brePOBox: '',
    uspsCRID: '',
    uspsMID: '',
    uspsPermit: ''
  });

  // ========== API CALLS ==========
  // Fetch all clients on component mount (First API)
  useEffect(() => {
    fetchClientPortalData();
  }, []);

  useEffect(() => {
    if (!selectedClient?.id) return;

    const savedImage =
      localStorage.getItem(getProfileImageKey(selectedClient.id));

    if (savedImage) {
      setProfileImage(savedImage); // base64/url
    } else {
      setProfileImage(null); // fallback to backend/avatar
    }

    setSelectedImageFile(null);
  }, [selectedClient]);

  // Fetch detailed client data when selectedClient changes (Second API)
  useEffect(() => {
    if (selectedClient && selectedClient.originalData?.clientId) {
      const clientId = selectedClient.originalData.clientId;
      fetchClientDetails(clientId);
      fetchClientContactsList(clientId);
    }
  }, [selectedClient]);

  const fetchClientPortalData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await GetClientPortal();

      if (response && response.isSuccess) {
        const clientList = response.data || [];

        if (clientList.length > 0) {
          const formattedClients = clientList.map((client) => ({
            id: client.clientId || client.id,
            name: client.clientName || client.name || `Client ${client.clientId}`,
            position: client.position || "Position not specified",
            phone: client.mainPhoneNumber || client.phone || "Not available",
            address: client.physicalAddress || client.address || "Address not available",
            email: client.email || "Email not available",
            clientCode: client.clientCode || "",
            originalData: client
          }))
            .sort((a, b) =>
              a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
            );

          setClients(formattedClients);

          // Try to select the initial client if passed from Dashboard
          let initialClient = null;
          if (initialClientId) {
            initialClient = formattedClients.find(
              client => client.originalData?.clientId?.toString() === initialClientId.toString()
            );
          }

          // If initial client found, select it; otherwise select first client
          setSelectedClient(initialClient || formattedClients[0]);

        } else {
          setError('No clients found in the API response.');
          setClients([]);
        }
      } else {
        setError(response?.error || 'Failed to fetch client list');
        setClients([]);
      }

    } catch (err) {

      setError(`Error: ${err.message || 'Failed to load client data'}`);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (!selectedClient?.id) return;

    fetchClientProfileImage(selectedClient.id);

    setSelectedImageFile(null);
  }, [selectedClient]);

  const handleSaveProfileImage = async () => {
    if (!selectedImageFile || !selectedClient?.id) return;

    try {
      await UploadClientProfilePicture(selectedClient.id, selectedImageFile);

      showNotification("Profile picture uploaded successfully");

      // ✅ Immediately fetch updated image
      await fetchClientProfileImage(selectedClient.id);

      setSelectedImageFile(null);

    } catch (err) {
      showNotification("Failed to save profile picture", "error");
    }
  };

  // ========== DELETE PROFILE PICTURE ==========
  const handleRemoveProfileImage = async () => {
    if (!selectedClient?.id || !hasRealProfileImage) return;

    try {
      setDeletingImage(true);

      await DeleteClientProfile(selectedClient.id);

      // Clear local state so the avatar shows immediately
      setProfileImage(null);
      setSelectedImageFile(null);

      // Also clear any cached localStorage entry for this client
      localStorage.removeItem(getProfileImageKey(selectedClient.id));

      showNotification("Profile picture removed successfully");

      // Close modal after successful delete
      setIsProfileModalOpen(false);

    } catch (err) {

      showNotification("Failed to remove profile picture", "error");
    } finally {
      setDeletingImage(false);
    }
  };
  const fetchClientProfileImage = async (clientId) => {
    try {
      setLoadingProfileImage(true); // ✅ loader ON
      const res = await GetClientProfilePicture(clientId);

      if (res && res.imageBase64) {
        const mimeType = res.contentType || 'image/jpeg';
        console.log("mimeType", mimeType)
        const dataUri = res.imageBase64.startsWith('data:')
          ? res.imageBase64
          : `data:${mimeType};base64,${res.imageBase64}`;
        setProfileImage(dataUri);
      } else if (res && res.downloadUrl) {
        setProfileImage(res.downloadUrl);
      } else {
        setProfileImage(null);
      }
    } catch (err) {

      setProfileImage(null);
    } finally {
      setLoadingProfileImage(false)
    }
  };


  const fetchClientDetails = async (clientId) => {
    try {
      setLoadingDetails(true);
      setErrorDetails(null);

      const response = await GetParticularClientList(clientId);


      if (response && response.isSuccess) {
        const data = response.data;
        setClientDetails(data);

        // Update form data with postal info from API
        setFormData(prev => ({
          ...prev,
          uspsAuthCode: data.npAuthorizationNumber || '',
          cagingPOBox: data.cagingPOBoxNumber || '',
          brePOBox: data.brepoBoxNumber || '',
          uspsCRID: data.crid || '',
          uspsMID: data.mid || '',
          uspsPermit: data.permitNumber || ''
        }));

      } else {
        setErrorDetails(response?.error || 'Failed to fetch client details');
      }
    } catch (err) {

      setErrorDetails(`Error: ${err.message || 'Failed to load client details'}`);
    } finally {
      setLoadingDetails(false);
    }
  };

  const fetchClientContactsList = async (clientId) => {
    try {
      setLoadingContacts(true);
      setErrorContacts(null);

      // Try to fetch actual contacts from API
      let apiContacts = [];
      try {
        const response = await GetClientContact(clientId);
        if (response && response.isSuccess && response.data) {
          apiContacts = response.data.map(contact => transformAPIContactToLocal(contact));
        }


      } catch (apiError) {
        return apiError
      }

      // If no contacts from API, create from client details
      if (apiContacts.length === 0 && clientDetails) {
        apiContacts = createContactsFromClientDetails(clientDetails);
      }

      setContacts(apiContacts);

    } catch (err) {
      setErrorContacts(`Error: ${err.message || 'Failed to load contacts'}`);
    } finally {
      setLoadingContacts(false);
    }
  };

  const transformAPIContactToLocal = (apiContact) => {
    // Get timezone mapping
    const timeZoneMap = {
      1: 'ET',
      2: 'CT',
      3: 'MT',
      4: 'PT',
      5: 'AKT',
      6: 'HST'
    };

    const timeZoneID = apiContact.timeZoneID;
    const timeZoneCode = timeZoneMap[timeZoneID] || '';

    return {
      id: apiContact.clientContactID || Date.now(),
      firstName: apiContact.contactFirstName || '',
      lastName: apiContact.contactLastName || '',
      fullName: `${apiContact.contactFirstName || ''} ${apiContact.contactLastName || ''}`.trim(),
      jobTitle: apiContact.contactPosition || '',
      timeZone: timeZoneCode,
      timeZoneName: apiContact.timeZoneName || '',
      email: apiContact.contactEmail || '',
      phone: apiContact.contactPhone || '',
      address: apiContact.contactAddress || '',
      city: apiContact.contactCity || '',
      state: apiContact.contactStateID,
      zipCode: apiContact.contactZipCode || '',
      // API fields for saving
      clientContactID: apiContact.clientContactID,
      contactFirstName: apiContact.contactFirstName || '',
      contactLastName: apiContact.contactLastName || '',
      contactPosition: apiContact.contactPosition || '',
      contactPhone: apiContact.contactPhone || '',
      contactEmail: apiContact.contactEmail || '',
      contactAddress: apiContact.contactAddress || '',
      contactCity: apiContact.contactCity || '',
      contactStateID: apiContact.contactStateID || 0,
      contactStateName: apiContact.stateName,
      contactZipCode: apiContact.contactZipCode || '',
      timeZoneID: apiContact.timeZoneID || 0,
      timeZoneName: apiContact.timeZoneName || ''
    };
  };

  const createContactsFromClientDetails = (clientData) => {
    const contactsFromDetails = [];

    // Add Account Manager as contact if available
    if (clientData.accountManager) {
      contactsFromDetails.push({
        id: 1,
        firstName: clientData.accountManager.split(' ')[0] || 'Account',
        lastName: clientData.accountManager.split(' ').slice(1).join(' ') || 'Manager',
        fullName: clientData.accountManager,
        jobTitle: 'Account Manager',
        timeZone: 'ET',
        email: clientData.email || 'account.manager@example.com',
        phone: clientData.mainPhoneNumber || '+1 (555)-555-5555',
        address: clientData.physicalAddress || 'Address not available',
        city: 'Springfield',
        state: 'IL',
        zipCode: '12345',
        clientContactID: null,
        contactFirstName: clientData.accountManager.split(' ')[0] || 'Account',
        contactLastName: clientData.accountManager.split(' ').slice(1).join(' ') || 'Manager',
        contactPosition: 'Account Manager',
        contactPhone: clientData.mainPhoneNumber || '+1 (555)-555-5555',
        contactEmail: clientData.email || 'account.manager@example.com',
        contactAddress: clientData.physicalAddress || 'Address not available',
        contactCity: 'Springfield',
        contactStateID: 0,
        contactZipCode: '12345',
        timeZoneID: 0
      });
    }

    // Add Production Manager as contact if available
    if (clientData.productionManager) {
      contactsFromDetails.push({
        id: 2,
        firstName: clientData.productionManager.split(' ')[0] || 'Production',
        lastName: clientData.productionManager.split(' ').slice(1).join(' ') || 'Manager',
        fullName: clientData.productionManager,
        jobTitle: 'Production Manager',
        timeZone: 'CT',
        email: 'production.manager@example.com',
        phone: '+1 (555)-555-5556',
        address: clientData.physicalAddress || 'Address not available',
        city: 'Chicago',
        state: 'IL',
        zipCode: '60601',
        clientContactID: null,
        contactFirstName: clientData.productionManager.split(' ')[0] || 'Production',
        contactLastName: clientData.productionManager.split(' ').slice(1).join(' ') || 'Manager',
        contactPosition: 'Production Manager',
        contactPhone: '+1 (555)-555-5556',
        contactEmail: 'production.manager@example.com',
        contactAddress: clientData.physicalAddress || 'Address not available',
        contactCity: 'Chicago',
        contactStateID: 0,
        contactZipCode: '60601',
        timeZoneID: 0
      });
    }

    // Add Consultant as contact if available
    if (clientData.consultant) {
      contactsFromDetails.push({
        id: 3,
        firstName: clientData.consultant.split(' ')[0] || 'Consultant',
        lastName: clientData.consultant.split(' ').slice(1).join(' ') || '',
        fullName: clientData.consultant,
        jobTitle: 'Consultant',
        timeZone: 'ET',
        email: 'consultant@example.com',
        phone: '+1 (555)-555-5557',
        address: clientData.physicalAddress || 'Address not available',
        city: 'New York',
        state: 'NY',
        zipCode: '10001',
        clientContactID: null,
        contactFirstName: clientData.consultant.split(' ')[0] || 'Consultant',
        contactLastName: clientData.consultant.split(' ').slice(1).join(' ') || '',
        contactPosition: 'Consultant',
        contactPhone: '+1 (555)-555-5557',
        contactEmail: 'consultant@example.com',
        contactAddress: clientData.physicalAddress || 'Address not available',
        contactCity: 'New York',
        contactStateID: 0,
        contactZipCode: '10001',
        timeZoneID: 0
      });
    }

    return contactsFromDetails;
  };

  // ========== HELPER FUNCTIONS ==========
  const openModalWithLocalContact = (contact) => {
    // Get state name from contact data (prefer contactStateName from API)
    const stateName = contact.contactStateName || contact.state || '';

    // Prepare complete contact data from local state
    const contactToEdit = {
      ...contact,
      // Ensure all fields are present
      firstName: contact.firstName || contact.contactFirstName || '',
      lastName: contact.lastName || contact.contactLastName || '',
      jobTitle: contact.jobTitle || contact.contactPosition || '',
      email: contact.email || contact.contactEmail || '',
      phone: contact.phone || contact.contactPhone || '',
      address: contact.address || contact.contactAddress || '',
      city: contact.city || contact.contactCity || '',
      // Pass state name for dropdown display
      state: stateName,
      stateID: contact.contactStateID || 0,
      zipCode: contact.zipCode || contact.contactZipCode || '',
      // Use timeZoneID for dropdown selection
      timeZoneID: contact.timeZoneID || 0,
      timeZoneName: contact.timeZoneName || '',
      // For backward compatibility
      timeZone: contact.timeZone || ''
    };

    setModalMode('edit');
    setSelectedContact(contactToEdit);
    setIsContactModalOpen(true);
  };

  const checkIfFavorite = (clientId) => {
    const savedFavorites = localStorage.getItem('clientFavorites');
    if (!savedFavorites || !clientId) return false;

    try {
      const favorites = JSON.parse(savedFavorites);
      return favorites[clientId]?.isFavorite || false;
    } catch (err) {
      return false;
    }
  };

  // ========== EVENT HANDLERS ==========
  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setIsDropdownOpen(false);
    setClientDetails(null);
    setContacts([]);
    setErrorDetails(null);
    setErrorContacts(null);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.client-select-dropdown')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  // Contact Modal Handlers
  const handleAddContact = () => {
    setModalMode('add');
    setSelectedContact(null);
    setIsContactModalOpen(true);
  };

  const handleEditContact = async (contact) => {

    if (!selectedClient || !selectedClient.originalData?.clientId) {
      return;
    }

    const clientId = selectedClient.originalData.clientId;
    const contactId = contact.clientContactID;

    // Check if this is an actual API contact (has clientContactID)
    if (contactId) {
      try {

        setLoadingContactDetails(true);

        const response = await GetClientContactDetailsById(clientId, contactId);
        if (response && (response.isSuccess || response.success) && response.data) {
          const apiContact = response.data;

          const timeZoneMap = {
            1: 'ET',
            2: 'CT',
            3: 'MT',
            4: 'PT',
            5: 'AKT',
            6: 'HST'
          };

          const timeZoneID = apiContact.timeZoneID;
          const timeZoneCode = timeZoneMap[timeZoneID] || '';
          const contactToEdit = {
            id: apiContact.clientContactID || Date.now(),
            firstName: apiContact.contactFirstName || '',
            lastName: apiContact.contactLastName || '',
            fullName: `${apiContact.contactFirstName || ''} ${apiContact.contactLastName || ''}`.trim(),
            jobTitle: apiContact.contactPosition || '',
            timeZone: timeZoneCode,
            timeZoneName: apiContact.timeZoneName || '',
            email: apiContact.contactEmail || '',
            phone: apiContact.contactPhone || '',
            address: apiContact.contactAddress || '',
            city: apiContact.contactCity || '',
            state: apiContact.stateName || apiContact.stateName || '',
            stateID: apiContact.contactStateID || '',
            zipCode: apiContact.contactZipCode || '',
            clientContactID: apiContact.clientContactID,
            contactFirstName: apiContact.contactFirstName || '',
            contactLastName: apiContact.contactLastName || '',
            contactPosition: apiContact.contactPosition || '',
            contactPhone: apiContact.contactPhone || '',
            contactEmail: apiContact.contactEmail || '',
            contactAddress: apiContact.contactAddress || '',
            contactCity: apiContact.contactCity || '',
            contactStateID: apiContact.contactStateID || 0,
            contactStateName: apiContact.stateName || apiContact.stateName || '',
            contactZipCode: apiContact.contactZipCode || '',
            timeZoneID: apiContact.timeZoneID || 0,
            timeZoneName: apiContact.timeZoneName || ''
          };
          setModalMode('edit');
          setSelectedContact(contactToEdit);
          setIsContactModalOpen(true);
        } else {
          openModalWithLocalContact(contact);
        }
      } catch (err) {
        openModalWithLocalContact(contact);
      } finally {
        setLoadingContactDetails(false);
      }
    } else {
      openModalWithLocalContact(contact);
    }
  };

  const handleCloseModal = () => {
    setIsContactModalOpen(false);
    setSelectedContact(null);
    setLoadingContactDetails(false);
  };

  // ✅ UPDATED: handleSaveContact now shows a success toaster (via SuccessMessage)
  // when a contact is added/updated, and a warning toaster if something goes wrong.
  // NOTE: this function only mutates local state today — it doesn't call a real
  // save API. If ContactModal performs the actual API call, make sure it either
  // throws on failure (so the catch below fires) or surfaces the error some other
  // way so we can route it into the warning toaster.
  const handleSaveContact = async (contactData, contactId) => {
    try {
      if (modalMode === 'add') {
        const newContact = {
          id: Date.now(),
          firstName: contactData.firstName || '',
          lastName: contactData.lastName || '',
          fullName: `${contactData.firstName} ${contactData.lastName}`.trim(),
          jobTitle: contactData.jobTitle || '',
          timeZone: contactData.timeZone || '',
          email: contactData.email || '',
          phone: contactData.phone || '',
          address: contactData.address || '',
          city: contactData.city || '',
          state: contactData.contactStateID || '',
          zipCode: contactData.zipCode || '',
          clientContactID: contactData.clientContactID || null,
          contactFirstName: contactData.contactFirstName || contactData.firstName || '',
          contactLastName: contactData.contactLastName || contactData.lastName || '',
          contactPosition: contactData.contactPosition || contactData.jobTitle || '',
          contactPhone: contactData.contactPhone || contactData.phone || '',
          contactEmail: contactData.contactEmail || contactData.email || '',
          contactAddress: contactData.contactAddress || contactData.address || '',
          contactCity: contactData.contactCity || contactData.city || '',
          contactStateID: contactData.contactStateID || 0,
          contactZipCode: contactData.contactZipCode || contactData.zipCode || '',
          timeZoneID: contactData.timeZoneID || 0
        };


        setContacts(prevContacts => [...prevContacts, newContact]);

        // ✅ Success toaster for Add Contact
        setContactMessage({
          show: true,
          message: 'Contact added successfully.',
          type: 'success',
        });
      } else {


        setContacts(prevContacts =>
          prevContacts.map(contact => {
            const isTargetContact =
              contact.id === contactId ||
              contact.clientContactID === contactId ||
              (contactData.clientContactID && contact.clientContactID === contactData.clientContactID);

            if (isTargetContact) {

              const updatedContact = {
                ...contact,
                firstName: contactData.firstName || contact.firstName,
                lastName: contactData.lastName || contact.lastName,
                fullName: `${contactData.firstName || contact.firstName} ${contactData.lastName || contact.lastName}`.trim(),
                jobTitle: contactData.jobTitle || contact.jobTitle,
                timeZone: contactData.timeZone || contact.timeZone,
                email: contactData.email || contact.email,
                phone: contactData.phone || contact.phone,
                address: contactData.address || contact.address,
                city: contactData.city || contact.city,
                state: contactData.stateName || contact.state,
                zipCode: contactData.zipCode || contact.zipCode,
                clientContactID: contactData.clientContactID || contact.clientContactID,
                contactFirstName: contactData.contactFirstName || contact.contactFirstName || contactData.firstName || contact.firstName,
                contactLastName: contactData.contactLastName || contact.contactLastName || contactData.lastName || contact.lastName,
                contactPosition: contactData.contactPosition || contact.contactPosition || contactData.jobTitle || contact.jobTitle,
                contactPhone: contactData.contactPhone || contact.contactPhone || contactData.phone || contact.phone,
                contactEmail: contactData.contactEmail || contact.contactEmail || contactData.email || contact.email,
                contactAddress: contactData.contactAddress || contact.contactAddress || contactData.address || contact.address,
                contactCity: contactData.contactCity || contact.contactCity || contactData.city || contact.city,
                contactStateID: contactData.contactStateID || contact.contactStateID || 0,
                contactStateName: contactData.contactStateName || contact.contactStateName || '',
                contactZipCode: contactData.contactZipCode || contact.contactZipCode || contactData.zipCode || contact.zipCode,
                timeZoneID: contactData.timeZoneID || contact.timeZoneID || 0,
                timeZoneName: contactData.timeZoneName || contact.timeZoneName || ''
              };


              return updatedContact;
            }
            return contact;
          })
        );

        // ✅ Success toaster for Edit/Update Contact
        setContactMessage({
          show: true,
          message: 'Contact updated successfully.',
          type: 'success',
        });
      }
    } catch (err) {
      // ✅ Warning toaster if add/update fails
      setContactMessage({
        show: true,
        message: `Failed to ${modalMode === 'add' ? 'add' : 'update'} contact: ${err?.message || 'Please try again.'}`,
        type: 'error',
      });
    } finally {
      handleCloseModal();
    }
  };

  // ========== DATA PROCESSING ==========
  const getDisplayData = () => {
    let data;

    if (clientDetails) {
      data = {
        name: clientDetails.clientName || selectedClient?.name || "Loading...",
        position: clientDetails.position || selectedClient?.position || "Position",
        phone: clientDetails.mainPhoneNumber || selectedClient?.phone || "Not available",
        address: clientDetails.physicalAddress || selectedClient?.address || "Address not available",
        email: clientDetails.email || selectedClient?.email || "Email not available",
        clientCode: clientDetails.clientCode || selectedClient?.clientCode || "",
        accountManager: clientDetails.accountManager || "",
        productionManager: clientDetails.productionManager || "",
        listManager: clientDetails.listManager || "",
        consultant: clientDetails.consultant || "",
        creativeDirector: clientDetails.creativeDirector || "",
        creativeFee: clientDetails.creativeFee || 0,
        designFee: clientDetails.designFee || 0,
        acctType: clientDetails.acctType || "",
        sageClientCode: clientDetails.sageClientCode || "",
        salesCommission: clientDetails.salesCommission || 0,
        contractDate: clientDetails.contractDate || "",
        npAuthorizationNumber: clientDetails.npAuthorizationNumber || '',
        cagingPOBoxNumber: clientDetails.cagingPOBoxNumber || '',
        brepoBoxNumber: clientDetails.brepoBoxNumber || '',
        crid: clientDetails.crid || '',
        mid: clientDetails.mid || '',
        permitNumber: clientDetails.permitNumber || '',

        grossRevenue: clientDetails.grossRevenue || 0,
        grossCost: clientDetails.grossCost || 0,
        netRevenue: clientDetails.netRevenue || 0,
        totalROI: clientDetails.totalROI || 0,
        grossRevenuePL: clientDetails.grossRevenuePL || 0,
        grossCostPL: clientDetails.grossCostPL || 0,
        netRevenuePL: clientDetails.netRevenuePL || 0,
        lastTotalROI: clientDetails.lastTotalROI || 0
      };
    } else {
      data = {
        name: selectedClient?.name || "No client selected",
        position: selectedClient?.position || "Not specified",
        phone: selectedClient?.phone || "Not available",
        address: selectedClient?.address || "Not available",
        email: selectedClient?.email || "Not available",
        clientCode: selectedClient?.clientCode || "",
        accountManager: "",
        productionManager: "",
        listManager: "",
        consultant: "",
        creativeDirector: "",
        creativeFee: 0,
        designFee: 0,
        acctType: "",
        sageClientCode: "",
        salesCommission: 0,
        contractDate: "",
        grossRevenue: 0,
        grossCost: 0,
        netRevenue: 0,
        totalROI: 0,
        grossRevenuePL: 0,
        grossCostPL: 0,
        netRevenuePL: 0,
        lastTotalROI: 0
      };
    }

    // ========== RBAC: STRIP HIDDEN FIELDS AT THE SOURCE ==========
    // Null them here so no child component (Summary/Metrics/Jobs) can
    // accidentally render restricted data.
    if (!fieldPerms.creativeFee) data.creativeFee = null;
    if (!fieldPerms.designFee) data.designFee = null;
    if (!fieldPerms.contractDate) data.contractDate = null;
    if (!fieldPerms.sageClientCode) data.sageClientCode = null;
    if (!fieldPerms.salesCommission) data.salesCommission = null;
    if (!fieldPerms.accountingType) data.acctType = null;

    // Internal cost/net/ROI metrics — blocked when Metrics tab is blocked
    if (!canViewMetricsTab) {
      data.grossCost = null;
      data.netRevenue = null;
      data.totalROI = null;
      data.grossCostPL = null;
      data.netRevenuePL = null;
      data.lastTotalROI = null;
    }

    return data;
  };

  const displayData = getDisplayData();

  // ========== COMPONENT STATE & EFFECTS ==========
  const [isFavorite, setIsFavorite] = useState(false);

  // Update favorite status when selectedClient changes
  useEffect(() => {
    if (selectedClient && selectedClient.id) {
      const favoriteStatus = checkIfFavorite(selectedClient.id);
      setIsFavorite(favoriteStatus);
    }
  }, [selectedClient]);

  // Toggle favorite handler with toast notifications
  const handleFavoriteToggle = async () => {
    if (!selectedClient) return;

    // Prevent multiple clicks while updating
    if (updatingFavorite) return;

    const clientId = selectedClient.id;
    const newStatus = !isFavorite;
    const originalStatus = isFavorite;

    try {
      setUpdatingFavorite(true);

      // Get existing favorites
      const savedFavorites = localStorage.getItem('clientFavorites');
      let favorites = savedFavorites ? JSON.parse(savedFavorites) : {};

      if (newStatus) {
        // Add to favorites
        favorites[clientId] = {
          isFavorite: true,
          clientName: selectedClient.name,
          updatedAt: new Date().toISOString()
        };
      } else {
        // Remove from favorites
        delete favorites[clientId];
      }

      // Save to localStorage
      localStorage.setItem('clientFavorites', JSON.stringify(favorites));

      // Update state optimistically
      setIsFavorite(newStatus);

      // Show toast notification for favorite/unfavorite
      showNotification(
        `${selectedClient.name} ${newStatus ? 'added to' : 'removed from'} favorites`,
        'success'
      );

      // Call API
      await MarkFavorite(clientId, newStatus);


    } catch (err) {
      console.error('API error while updating favorite:', err);

      // Revert the favorite status on error
      setIsFavorite(originalStatus);

      // Revert localStorage
      const savedFavorites = localStorage.getItem('clientFavorites');
      let favorites = savedFavorites ? JSON.parse(savedFavorites) : {};

      if (originalStatus) {
        favorites[clientId] = {
          isFavorite: true,
          clientName: selectedClient.name,
          updatedAt: new Date().toISOString()
        };
      } else {
        delete favorites[clientId];
      }
      localStorage.setItem('clientFavorites', JSON.stringify(favorites));

      // Show error toast for favorite/unfavorite failure
      showNotification(
        `Failed to update favorite: ${err.message || 'Please try again'}`,
        'error'
      );
    } finally {
      setUpdatingFavorite(false);
    }
  };

  // Helper function for phone formatting - XXX-XXX-XXXX format
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';

    const cleaned = phone.toString().replace(/\D/g, '');

    if (cleaned.length === 10) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `${cleaned.slice(0, 1)}-${cleaned.slice(1, 4)}-${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  // CustomStarIcon component with loading state
  const CustomStarIcon = ({ isFavorite, onToggle, isLoading }) => {
    return (
      <span
        onClick={isLoading ? null : onToggle}
        style={{
          fontSize: '25px',
          color: isLoading ? '#ccc' : (isFavorite ? '#FFD700' : '#ccc'),
          cursor: isLoading ? 'not-allowed' : 'pointer',
          display: 'inline-block',
          transition: 'color 0.3s ease',
          opacity: isLoading ? 0.6 : 1
        }}
        title={isLoading ? 'Updating...' : (isFavorite ? 'Remove from favorites' : 'Add to favorites')}
      >
        {isFavorite ? '★' : '☆'}
      </span>
    );
  };

  // Render contact cards with loading indicator for edit
  const renderContactCards = () => {
    if (loadingContacts) {
      return <AisScreenLoader />;
    }


    if (errorContacts) {
      return (
        <div className="error-contacts-container p-3 bg-light rounded mb-3">
          <div className="alert alert-warning mb-0" role="alert">
            <small>{errorContacts}</small>
          </div>
        </div>
      );
    }

    if (contacts.length === 0) {
      return (
        <div className="no-contacts-message p-4 text-center bg-light rounded">
          <p className="mb-0 text-muted">No contacts available. Click "Add Contact" to add a new contact.</p>
        </div>
      );
    }
    return contacts.map((contact) => (
      <div key={contact.id || contact.clientContactID} className="contact-info-card mb-3">
        <div className="contact-header-section">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <div className="contact-logo-style">
                {contact.firstName[0].toUpperCase()}{contact.lastName[0].toUpperCase()}

              </div>
              <div className="d-flex flex-column ms-3">
                <h3 className="contact-person-name mb-0">{contact.fullName}</h3>
                <p className="contact-job-title mb-0">{contact.jobTitle}</p>
              </div>
            </div>

            <div className="d-flex gap-2">
              <button
                className="btn btn-link p-0 icon-button"
                aria-label="Edit contact"
                onClick={() => handleEditContact(contact)}
                disabled={loadingContactDetails}
              >
                {loadingContactDetails && contact.clientContactID === selectedContact?.clientContactID ? (
                  <AisScreenLoader />
                ) : (
                  <Icon
                    name="edit"
                    size={20}
                    alt="Edit contact"
                  />
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="contact-details-section" style={{ paddingLeft: "70px" }}>
          <div className="contact-detail-item mb-2">
            <Icon
              name="email"
              size={16}
              alt="Email"
              className="me-2"
            />
            {contact.email && contact.email !== "Email not available" ? (
              <a
                href={`https://outlook.office.com/mail/deeplink/compose?to=${contact.email}`}
                target="_blank"
                rel="noopener noreferrer"
                className="contact-detail-value client-detail-email"
              >
                {contact.email}
              </a>
            ) : (
              <span className="contact-detail-value client-detail-email">Email not available</span>
            )}
          </div>

          <div className="contact-detail-item mb-2">
            <Icon
              name="phone"
              size={16}
              alt="Phone"
              className="me-2"
            />
            <span className="contact-detail-value"> {formatPhoneNumber(contact.phone)}</span>
          </div>

          <div className="contact-detail-item mb-2">
            <Icon
              name="physical-mailing-address"
              size={16}
              alt="Address"
              className="me-2"
            />
            <span className="contact-detail-value">
              {contact.address}, {contact.city}, {contact.contactStateName}, {contact.zipCode}
            </span>
          </div>

          <div className="contact-detail-item">
            <Icon
              name="timezone"
              size={16}
              alt="Time zone"
              className="me-2"
            />
            <span className="contact-detail-value">
              {contact.timeZoneName ||
                (contact.timeZone === 'ET' ? 'Eastern Time (ET)' :
                  contact.timeZone === 'CT' ? 'Central Time (CT)' :
                    contact.timeZone === 'MT' ? 'Mountain Time (MT)' :
                      contact.timeZone === 'PT' ? 'Pacific Time (PT)' :
                        contact.timeZone === 'AKT' ? 'Alaska Time (AKT)' :
                          contact.timeZone === 'HST' ? 'Hawaii Time (HST)' :
                            contact.timeZone || 'Time zone not specified')}
            </span>
          </div>
        </div>
      </div>
    ));
  };

  // ========== TAB CONTENT RENDERERS ==========
  const renderTabContent = () => {
    switch (activeTab) {
      case 'summary':
        return <Summary displayData={displayData} handleAddContact={handleAddContact} selectedClient={selectedClient}
          renderContactCards={renderContactCards}
          loadingContactDetails={loadingContactDetails}
          clientDetails={clientDetails}
          handleInputChange={handleInputChange}
          fieldPerms={fieldPerms} // ✅ RBAC: Summary hides sections based on these flags
        />
      case 'jobs':
        return (
          <>
            {selectedClient && selectedClient.id ? (
              <Jobs clientId={selectedClient.id}
                loadingDetails={loadingDetails}
                errorDetails={errorDetails}
                displayData={displayData}
                profileImage={imageToShow}
              />
            ) : (
              <div className="alert alert-warning p-3">
                <small>Please select a client first to view Jobs.</small>
              </div>
            )}
          </>
        );

      case 'metrics':
        // ✅ RBAC: metrics contains internal cost/net/ROI data
        return canViewMetricsTab ? <Matrics /> : null;

      case 'tailies':
        return (
          <div className="tailies-tab-content">
            {selectedClient && selectedClient.id ? (
              // <Tallies clientId={selectedClient.id} />
              <Tallies
                clientId={selectedClient.id}
                clientName={displayData.name}
              />

            ) : (
              <div className="alert alert-warning p-3">
                <small>Please select a client first to view tallies.</small>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // ========== LOADING STATE ==========
  if (loading) {
    return (
      <>
        <Notification />
        <AisScreenLoader />
      </>
    );
  }


  // ========== ERROR STATE ==========
  if (error && clients.length === 0) {
    return (
      <div className="client-dashboard-container">
        <Notification />
        <div className="error-container p-4 text-center">
          <div className="alert alert-danger" role="alert">
            <strong>Error Loading Clients:</strong> {error}
          </div>
          <button
            className="btn btn-primary mt-3"
            onClick={fetchClientPortalData}
          >
            Retry Loading Data
          </button>
          <button
            className="btn btn-secondary mt-3 ms-2"
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const extractEmail = (value) => {
    const match = value.match(/<(.+?)>/);
    return match ? match[1] : value.trim();
  };

  const cleanEmail = extractEmail(displayData.email);

  // ========== MAIN RENDER ==========
  return (
    <div className="client-dashboard-container">
      <Notification />

      {/* ✅ NEW: Success/Warning modal-style toaster for Add/Edit Contact */}
      <SuccessMessage
        message={contactMessage.show ? contactMessage.message : null}
        type={contactMessage.type}
        onClose={closeContactMessage}
      />

      <div className="dashboard-layout">
        <div className="main-content-area-client">
          <div className="content-section">
            {/* Client Selection Header */}
            <div className="client-select-header">
              <div className="client-header-container">
                <div className="client-select-wrapper">
                  <Icon
                    name="user-icon"
                    size={14}
                    alt="user-icon"
                  />
                  <span className="client-select-label" onClick={() => navigate('/dashboard')}>Client Select</span>
                  <div className="client-dropdown-wrapper">
                    <div
                      ref={dropdownRef}
                      className="client-select-dropdown"
                      tabIndex={0}
                      onKeyDown={handleKeyDown}
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsDropdownOpen(!isDropdownOpen);
                      }}
                    >
                      <span className="client-select-text">
                        / Client:
                        <span className="selected-client-name"> {displayData.name}</span>
                        <span
                          style={{
                            transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s',
                            display: 'inline-flex',
                            alignItems: 'center'
                          }}
                        >
                          <img
                            src="../assets/icon-down.png"
                            alt="dropdown"
                            style={{ width: '12px', height: '12px' }}
                          />
                        </span>
                      </span>
                      {isDropdownOpen && clients.length > 0 && (
                        <div className="client-dropdown-menu">
                          {clients.map((client) => (
                            <div
                              key={client.id}
                              ref={(el) => (itemRefs.current[client.id] = el)}
                              className={`dropdown-item 
              ${selectedClient?.id === client.id ? 'selected' : ''} 
              ${highlightedId === client.id ? 'highlighted' : ''}`}
                              onClick={() => handleClientSelect(client)}
                              onMouseEnter={() => setHighlightedId(client.id)}
                            >
                              <div className="dropdown-client-name">{client.name}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {loadingDetails && (
                <AisScreenLoader />
              )}

            </div>

            {/* Client Information Section */}
            <div className="client-info-section">
              <div className="profile-pic-container">

                {loadingProfileImage ? (
                  <AisScreenLoader />
                ) : (
                  <img
                    src={imageToShow}
                    alt="Profile"
                    className="profile-pic-image"
                  />
                )}
                {/* ✅ RBAC: hide the edit overlay entirely for restricted roles */}
                {canEditProfilePicture && (
                  <div
                    className="profile-pic-overlay"
                    onClick={() => setIsProfileModalOpen(true)}
                  >
                    <div className="overlay-content">
                      <div className="camera-icon">
                        <Icon name="camera-icon" size={30} alt="Phone" />
                      </div>
                      <div className="edit-text">Edit</div>
                    </div>
                  </div>
                )}

                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleImageSelect}
                />
              </div>
              <div>
                <div style={{ position: "relative", display: "inline-block" }}>
                  <h1 className="client-name">{displayData.name}</h1>
                  {!isConsultantOrClient && (
                    <div style={{
                      position: "absolute",
                      top: "-5px",
                      right: "-20px",
                      zIndex: 1
                    }}>
                      <CustomStarIcon
                        isFavorite={isFavorite}
                        onToggle={handleFavoriteToggle}
                        isLoading={updatingFavorite}
                      />
                    </div>
                  )}
                </div>

                <div className="client-details-grid">
                  <div className="client-detail">
                    <span className="detail-label">Type</span>
                    <span className="detail-value">{displayData.position}</span>
                  </div>
                  <div className="client-detail">
                    <span className="detail-label">Main Phone</span>
                    <span className="detail-value">{formatPhoneNumber(displayData.phone)}</span>
                  </div>
                  <div className="client-detail">
                    <span className="detail-label">Physical Address</span>
                    <span className="detail-value">{displayData.address}</span>
                  </div>

                  <div className="client-detail">
                    <span className="detail-label">Email</span>

                    {displayData.email && displayData.email !== "Email not available" ? (
                      <a
                        href={`https://outlook.office.com/mail/deeplink/compose?to=${displayData.email}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {cleanEmail}
                      </a>
                    ) : (
                      <span className="detail-value">Email Not available</span>
                    )}
                  </div>


                </div>
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="client-nav-tabs">
              <button
                className={`nav-tab ${activeTab === 'summary' ? 'active' : ''}`}
                onClick={() => setActiveTab('summary')}
              >
                Summary
              </button>
              <button
                className={`nav-tab ${activeTab === 'jobs' ? 'active' : ''}`}
                onClick={() => setActiveTab('jobs')}
              >
                Jobs
              </button>
              {/* ✅ RBAC: Metrics tab hidden for users without internal metric access */}
              {canViewMetricsTab && (
                <button
                  className={`nav-tab ${activeTab === 'metrics' ? 'active' : ''}`}
                  onClick={() => setActiveTab('metrics')}
                >
                  Metrics
                </button>
              )}
              <button
                className={`nav-tab ${activeTab === 'tailies' ? 'active' : ''}`}
                onClick={() => setActiveTab('tailies')}
              >
                Tallies
              </button>
            </div>

            {/* Dynamic Content Based on Active Tab */}
            {renderTabContent()}

            {/* Contact Modal */}
            {selectedClient && (
              <ContactModal
                isOpen={isContactModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveContact}
                contact={selectedContact}
                mode={modalMode}
                clientId={selectedClient?.originalData?.clientId || selectedClient?.id}
              />
            )}

            {/* Profile Picture Upload / Remove Modal */}
            {isProfileModalOpen && (
              <div className="profile-upload-backdrop">
                <div className="profile-upload-modal">

                  <h2 className="modal-title">Upload Photo</h2>

                  <div className="modal-avatar-preview">
                    <img src={imageToShow} alt="Preview" />
                  </div>

                  <div className="modal-actions">
                    <button
                      className="upload-btn"
                      onClick={() => fileInputRef.current.click()}
                      disabled={deletingImage}
                    >
                      Upload Photo
                    </button>

                    <button
                      className="remove-btn"
                      disabled={!hasRealProfileImage || deletingImage}
                      style={{
                        opacity: (!hasRealProfileImage || deletingImage) ? 0.5 : 1,
                        cursor: (!hasRealProfileImage || deletingImage) ? 'not-allowed' : 'pointer'
                      }}
                      onClick={handleRemoveProfileImage}
                    >
                      {deletingImage ? 'Removing...' : 'Remove'}
                    </button>
                  </div>

                  <p className="upload-hint">JPG or PNG. Max size 5MB</p>

                  <div className="modal-footer">
                    <button
                      className="cancel-btn"
                      disabled={deletingImage}
                      onClick={() => {
                        setIsProfileModalOpen(false);
                        setSelectedImageFile(null);
                        // Re-sync image with backend so canceling a preview restores the saved one
                        if (selectedClient?.id) {
                          fetchClientProfileImage(selectedClient.id);
                        }
                      }}
                    >
                      Cancel
                    </button>

                    <button
                      className="save-changes-btn"
                      disabled={!selectedImageFile || deletingImage}
                      onClick={() => {
                        handleSaveProfileImage();
                        setIsProfileModalOpen(false);
                      }}
                    >
                      Save
                    </button>
                  </div>

                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/png, image/jpeg"
                    hidden
                    onChange={handleImageSelect}
                  />

                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientDashboardMain;